// src/achats/DetailCommande.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Badge, Table, Spinner,
    Modal, Form
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailCommande() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [commande, setCommande] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReceptionModal, setShowReceptionModal] = useState(false);
    const [lignesRecues, setLignesRecues] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchCommande();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchCommande = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setCommande(result.data);
            if (result.data?.lignes) {
                setLignesRecues(result.data.lignes.map(l => ({
                    id_ligne_commande: l.id_ligne_commande,
                    quantite_commandee: l.quantite_commandee,
                    quantite_recue: l.quantite_recue || 0,
                    a_recevoir: 0
                })));
            }
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'annuler', id: commande.id_commande_fournisseur })
            });
            const result = await response.json();
            if (result.success) {
                setShowCancelModal(false);
                fetchCommande();
                showToast('success', 'Succès', 'Commande annulée avec succès');
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'annulation");
                setShowCancelModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
            setShowCancelModal(false);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleReception = async () => {
        setIsUpdating(true);
        try {
            const token = localStorage.getItem("token");
            const lignes = lignesRecues
                .filter(l => l.a_recevoir > 0)
                .map(l => ({ id_ligne_commande: l.id_ligne_commande, quantite_recue: l.a_recevoir }));
            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'marquer_recue',
                    id: commande.id_commande_fournisseur,
                    lignes_recues: lignes
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowReceptionModal(false);
                fetchCommande();
                showToast('success', 'Succès', 'Réception enregistrée avec succès');
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la réception");
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de la réception");
        } finally {
            setIsUpdating(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutBadge = (statut) => {
        const configs = {
            brouillon:       { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Brouillon', icon: 'ri-draft-line' },
            envoyee:         { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'Envoyée',   icon: 'ri-send-plane-line' },
            recue_partielle: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Partielle', icon: 'ri-time-line' },
            recue_complete:  { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Reçue',     icon: 'ri-check-double-line' },
            annulee:         { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Annulée',   icon: 'ri-close-circle-line' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement de la commande...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    if (error || !commande) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Commande non trouvée</h6>
                        <p className="text-muted small mb-3">{error || "La commande demandée n'existe pas"}</p>
                        <Button className="btn-modern-outline" onClick={() => navigate(-1)}>
                            <i className="ri-arrow-left-line me-1"></i>Retour
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const totalRecu = commande.lignes?.reduce((acc, l) => acc + (l.quantite_recue || 0), 0) || 0;
    const totalCommandee = commande.lignes?.reduce((acc, l) => acc + l.quantite_commandee, 0) || 0;
    const pourcentageRecu = totalCommandee > 0 ? (totalRecu / totalCommandee) * 100 : 0;

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-shopping-cart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">{commande.numero_commande}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-store-line"></i> {commande.fournisseur_nom}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i> {formatDate(commande.date_commande)}
                                    </span>
                                    {getStatutBadge(commande.statut)}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate(-1)}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            {commande.statut === 'brouillon' && (
                                <Link to={`/achats/commandesModifier/${commande.id_commande_fournisseur}`} className="btn-modern-outline">
                                    <i className="ri-pencil-line"></i> Modifier
                                </Link>
                            )}
                            {['envoyee', 'recue_partielle'].includes(commande.statut) && (
                                <Button className="btn-modern-success" onClick={() => setShowReceptionModal(true)}>
                                    <i className="ri-check-double-line"></i> Réceptionner
                                </Button>
                            )}
                            {['brouillon', 'envoyee'].includes(commande.statut) && (
                                <Button className="btn-modern-danger" onClick={() => setShowCancelModal(true)}>
                                    <i className="ri-close-circle-line"></i> Annuler
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-information-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Statut</span>
                                    <div className="mt-2">{getStatutBadge(commande.statut)}</div>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Montant TTC</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(commande.montant_ttc || 0)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Livraison prévue</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                        {commande.date_livraison_prevue ? formatDate(commande.date_livraison_prevue) : 'Non spécifiée'}
                                    </h3>
                                    {commande.date_livraison_prevue && (
                                        <span className={`kpi-unit-charte ${new Date(commande.date_livraison_prevue) < new Date() ? 'text-danger' : 'text-success'}`}>
                                            {new Date(commande.date_livraison_prevue) < new Date() ? 'En retard' : 'À temps'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Progression</span>
                                    <h3 className="kpi-value-charte">{totalRecu}/{totalCommandee}</h3>
                                    <div className="progress-bar-charte mt-1">
                                        <div className="progress-fill-charte" style={{ width: `${pourcentageRecu}%`, background: COLORS.primary }}></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== INFOS DÉTAILLÉES ===== */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-store-line"></i>
                                        </span>
                                        <h6>Fournisseur</h6>
                                    </div>
                                    <div className="info-row-charte"><span className="info-label-charte">Raison sociale</span><span className="info-value-charte fw-semibold">{commande.fournisseur_nom}</span></div>
                                    <div className="info-row-charte"><span className="info-label-charte">Code</span><span className="info-value-charte">{commande.code_fournisseur}</span></div>
                                    <div className="info-row-charte"><span className="info-label-charte">Téléphone</span><span className="info-value-charte">{commande.fournisseur_telephone || '—'}</span></div>
                                    <div className="info-row-charte"><span className="info-label-charte">Email</span><span className="info-value-charte">{commande.fournisseur_email || '—'}</span></div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-file-list-line"></i>
                                        </span>
                                        <h6>Détails</h6>
                                    </div>
                                    <div className="info-row-charte"><span className="info-label-charte">Date commande</span><span className="info-value-charte">{formatDate(commande.date_commande)}</span></div>
                                    <div className="info-row-charte"><span className="info-label-charte">Créée par</span><span className="info-value-charte">{commande.prenom_utilisateur} {commande.nom_utilisateur}</span></div>
                                    {commande.conditions_reglement && (
                                        <div className="info-row-charte"><span className="info-label-charte">Conditions</span><span className="info-value-charte">{commande.conditions_reglement}</span></div>
                                    )}
                                    <div className="info-row-charte"><span className="info-label-charte">Articles</span><span className="info-value-charte fw-semibold">{commande.lignes?.length || 0}</span></div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== TABLEAU PRODUITS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-stack-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Lignes de commande</span>
                                </div>
                                <span className="badge-header-light">{commande.lignes?.length || 0} article(s)</span>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Désignation</th>
                                            <th className="text-center">Qté</th>
                                            <th className="text-end">Prix unit.</th>
                                            <th className="text-center">Remise</th>
                                            <th className="text-end">Total HT</th>
                                            <th className="text-center pe-4">Réception</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {commande.lignes?.map(ligne => {
                                            const pourcentage = (ligne.quantite_recue / ligne.quantite_commandee) * 100;
                                            const complete = ligne.quantite_recue === ligne.quantite_commandee;
                                            return (
                                                <tr key={ligne.id_ligne_commande}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">{ligne.reference}</span>
                                                    </td>
                                                    <td className="fw-semibold">{ligne.designation}</td>
                                                    <td className="text-center">
                                                        <span className="quantity-badge-charte">{ligne.quantite_commandee}</span>
                                                    </td>
                                                    <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                    <td className="text-center">{ligne.remise ? `${ligne.remise}%` : '—'}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                        {formatCurrency(ligne.montant_ligne_ht || 0)}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <div className="progress-bar-charte" style={{ width: '60px' }}>
                                                                <div
                                                                    className="progress-fill-charte"
                                                                    style={{
                                                                        width: `${pourcentage}%`,
                                                                        background: complete ? COLORS.success : COLORS.warning
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span
                                                                className="status-badge-charte"
                                                                style={{
                                                                    background: complete
                                                                        ? 'linear-gradient(135deg, #10b981, #059669)'
                                                                        : 'linear-gradient(135deg, #f59e0b, #d97706)'
                                                                }}
                                                            >
                                                                {ligne.quantite_recue || 0}/{ligne.quantite_commandee}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== TOTAUX ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="card-header-gradient">
                            <h5 className="mb-0 text-white d-flex align-items-center">
                                <i className="ri-calculator-line me-2"></i>
                                Totaux
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="recap-line-charte d-flex justify-content-between mb-2">
                                <span className="text-muted">Total HT</span>
                                <span className="fw-bold">{formatCurrency(commande.montant_ht || 0)}</span>
                            </div>
                            <div className="recap-line-charte d-flex justify-content-between mb-2">
                                <span className="text-muted">TVA (18%)</span>
                                <span className="fw-semibold" style={{ color: COLORS.warning }}>{formatCurrency(commande.montant_tva || 0)}</span>
                            </div>
                            <div className="recap-line-charte d-flex justify-content-between mb-3">
                                <span className="text-muted">CSS (1%)</span>
                                <span className="fw-semibold" style={{ color: COLORS.info }}>{formatCurrency(commande.montant_css || 0)}</span>
                            </div>
                            <hr />
                            <div className="recap-line-charte d-flex justify-content-between">
                                <span className="fw-bold fs-5">Total TTC</span>
                                <span className="fw-bold fs-4" style={{ color: COLORS.primary }}>{formatCurrency(commande.montant_ttc || 0)}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL RÉCEPTION ===== */}
            <Modal show={showReceptionModal} onHide={() => setShowReceptionModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-inbox-archive-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Réceptionner la commande</Modal.Title>
                            <p className="text-white-50 mb-0 small">Saisissez les quantités reçues</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="table-responsive">
                        <Table className="align-middle mb-0 table-charte">
                            <thead>
                                <tr>
                                    <th>Produit</th>
                                    <th className="text-center">Commandé</th>
                                    <th className="text-center">Reçu</th>
                                    <th className="text-center">Restant</th>
                                    <th className="text-center">À recevoir</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignesRecues.map((ligne, index) => {
                                    const restant = ligne.quantite_commandee - ligne.quantite_recue;
                                    const produit = commande.lignes[index];
                                    return (
                                        <tr key={ligne.id_ligne_commande}>
                                            <td>
                                                <div className="fw-semibold">{produit?.designation}</div>
                                                <small className="text-muted">{produit?.reference}</small>
                                            </td>
                                            <td className="text-center">
                                                <span className="quantity-badge-charte">{ligne.quantite_commandee}</span>
                                            </td>
                                            <td className="text-center">
                                                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                                    {ligne.quantite_recue}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                                    {restant}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max={restant}
                                                    value={ligne.a_recevoir}
                                                    onChange={(e) => {
                                                        const newLignes = [...lignesRecues];
                                                        newLignes[index].a_recevoir = parseInt(e.target.value) || 0;
                                                        setLignesRecues(newLignes);
                                                    }}
                                                    size="sm"
                                                    className="text-center form-control-charte mx-auto"
                                                    style={{ width: '80px' }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button className="btn-modern-outline" onClick={() => setShowReceptionModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        className="btn-modern-success"
                        onClick={handleReception}
                        disabled={isUpdating || lignesRecues.every(l => l.a_recevoir === 0)}
                    >
                        {isUpdating ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-double-line me-2"></i>}
                        Confirmer la réception
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL ANNULATION ===== */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-alert-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Annuler la commande</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">
                        Êtes-vous sûr de vouloir annuler la commande <strong style={{ color: COLORS.primary }}>{commande.numero_commande}</strong> ?
                    </p>
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="commande-avatar-charte">
                                <i className="ri-shopping-cart-line"></i>
                            </div>
                            <div className="flex-grow-1">
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                    {commande.numero_commande}
                                </div>
                                <div className="text-muted small">
                                    <i className="ri-store-line me-1"></i>
                                    {commande.fournisseur_nom}
                                </div>
                                <div className="fw-bold mt-1" style={{ color: COLORS.primary }}>
                                    {formatCurrency(commande.montant_ttc || 0)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-information-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Cette action est irréversible et ne peut pas être annulée</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button className="btn-modern-outline" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                        Annuler
                    </Button>
                    <Button className="btn-modern-danger" onClick={handleConfirmCancel} disabled={isCancelling}>
                        {isCancelling ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-2"></i>}
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== INFO ROWS ===== */
                .info-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .info-row-charte:last-child { border-bottom: none; }
                .info-label-charte { color: #64748b; font-size: 0.85rem; }
                .info-value-charte { font-weight: 500; color: #1e293b; font-size: 0.9rem; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte { padding: 0.5rem 0; }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                    text-decoration: none;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .commande-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.2rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== INPUT ===== */
                .form-control-charte {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .form-control-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}